import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, CheckCircle, MessageCircle, RefreshCw } from 'lucide-react';
import { getAvailabilityForDateRange } from '../services/googleCalendar';

const BookingCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingData, setBookingData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    guestCount: '',
    additionalNotes: ''
  });

  // Real-time availability data from Google Calendar
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Google Calendar URL or Calendar ID
  // To get your Calendar ID:
  // 1. Go to Google Calendar Settings (https://calendar.google.com/calendar/r/settings)
  // 2. Click on your calendar name
  // 3. Scroll to "Integrate calendar" section
  // 4. Copy the "Calendar ID" (usually your email or a custom ID like: abc123@group.calendar.google.com)
  // 5. Set it as: REACT_APP_GOOGLE_CALENDAR_URL=your-calendar-id@group.calendar.google.com
  // Or update the default value below with your calendar ID
  const CALENDAR_URL = process.env.REACT_APP_GOOGLE_CALENDAR_URL || 'https://calendar.google.com/calendar/u/1/r/tasks?pli=1';

  // Fetch real-time availability from Google Calendar
  useEffect(() => {
    const fetchAvailability = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const today = new Date();
        const endDate = new Date(today);
        endDate.setDate(today.getDate() + 90); // Next 3 months
        
        const availabilityData = await getAvailabilityForDateRange(today, endDate, CALENDAR_URL);
        setAvailability(availabilityData);
        setLastUpdated(new Date());
      } catch (err) {
        console.error('Error fetching calendar availability:', err);
        setError('Failed to load calendar availability. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchAvailability();
    
    // Refresh availability every 5 minutes for real-time updates
    const refreshInterval = setInterval(fetchAvailability, 5 * 60 * 1000);
    
    return () => clearInterval(refreshInterval);
  }, [CALENDAR_URL]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const isDateAvailable = (date) => {
    if (!date) return false;
    const dateKey = date.toISOString().split('T')[0];
    return availability[dateKey]?.available === true;
  };

  const isDateFullyBooked = (date) => {
    if (!date) return false;
    const dateKey = date.toISOString().split('T')[0];
    const dayAvailability = availability[dateKey];
    return dayAvailability?.fullyBooked === true;
  };

  const refreshAvailability = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const today = new Date();
      const endDate = new Date(today);
      endDate.setDate(today.getDate() + 90);
      
      const availabilityData = await getAvailabilityForDateRange(today, endDate, CALENDAR_URL);
      setAvailability(availabilityData);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error refreshing calendar:', err);
      setError('Failed to refresh calendar. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getDateStatus = (date) => {
    if (!date) return 'empty';
    if (isDateFullyBooked(date)) return 'booked';
    if (isDateAvailable(date)) return 'available';
    return 'unavailable';
  };

  const handleDateClick = (date) => {
    if (date && isDateAvailable(date)) {
      setSelectedDate(date);
      setShowBookingForm(true);
    }
  };

  const handleBookingSubmit = (e) => {
    e.preventDefault();
    
    const whatsappMessage = `
Hi! I'd like to book an event with UBUNTU EVENTS MANAGEMENT:

📅 Preferred Date: ${selectedDate.toLocaleDateString('en-ZA', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })}

👤 Name: ${bookingData.name}
📧 Email: ${bookingData.email}
📱 Phone: ${bookingData.phone}
🎉 Event Type: ${bookingData.eventType}
👥 Guest Count: ${bookingData.guestCount}

📝 Additional Notes:
${bookingData.additionalNotes}

Please let me know if this date is available and what the next steps are. Thank you!
    `.trim();

    const whatsappUrl = `https://wa.me/27663286231?text=${encodeURIComponent(whatsappMessage)}`;
    window.open(whatsappUrl, '_blank');
    
    // Reset form
    setBookingData({
      name: '',
      email: '',
      phone: '',
      eventType: '',
      guestCount: '',
      additionalNotes: ''
    });
    setShowBookingForm(false);
    setSelectedDate(null);
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream to-white py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-6">
            <div className="bg-gold/10 p-4 rounded-full">
              <Calendar className="w-12 h-12 text-gold" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-playfair font-bold text-gray-800 mb-4">
            Catering Availability Calendar
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Check our availability and book your preferred date for your special event
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Calendar */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="card"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-playfair font-semibold text-gray-800">
                  {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                </h2>
                {lastUpdated && (
                  <p className="text-xs text-gray-500 mt-1">
                    Updated: {lastUpdated.toLocaleTimeString()}
                  </p>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={refreshAvailability}
                  disabled={loading}
                  className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50"
                  title="Refresh availability"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => navigateMonth(-1)}
                  className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  ←
                </button>
                <button
                  onClick={() => navigateMonth(1)}
                  className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  →
                </button>
              </div>
            </div>
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}
            
            {loading && !lastUpdated && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm flex items-center space-x-2">
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Loading calendar availability...</span>
              </div>
            )}

            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-4">
              {dayNames.map(day => (
                <div key={day} className="text-center text-sm font-semibold text-gray-600 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {getDaysInMonth(currentDate).map((date, index) => {
                const status = getDateStatus(date);
                const isSelected = selectedDate && date && selectedDate.getTime() === date.getTime();
                
                return (
                  <div
                    key={index}
                    className={`
                      aspect-square flex items-center justify-center text-sm font-medium rounded-lg cursor-pointer transition-all duration-200
                      ${status === 'empty' ? 'invisible' : ''}
                      ${status === 'available' ? 'bg-green-100 text-green-800 hover:bg-green-200 border border-green-300' : ''}
                      ${status === 'booked' ? 'bg-red-100 text-red-800 cursor-not-allowed border border-red-300' : ''}
                      ${status === 'unavailable' ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}
                      ${isSelected ? 'ring-2 ring-gold bg-gold/20' : ''}
                    `}
                    onClick={() => handleDateClick(date)}
                  >
                    {date && date.getDate()}
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="mt-6 flex flex-wrap gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-green-100 border border-green-300 rounded"></div>
                <span className="text-gray-600">Available</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-red-100 border border-red-300 rounded"></div>
                <span className="text-gray-600">Fully Booked</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-gray-100 rounded"></div>
                <span className="text-gray-600">Unavailable</span>
              </div>
            </div>
          </motion.div>

          {/* Booking Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-6"
          >
            {!showBookingForm ? (
              <div className="card text-center">
                <div className="flex justify-center mb-4">
                  <div className="bg-gold/10 p-4 rounded-full">
                    <Clock className="w-8 h-8 text-gold" />
                  </div>
                </div>
                <h3 className="text-2xl font-playfair font-semibold text-gray-800 mb-4">
                  Select a Date
                </h3>
                <p className="text-gray-600 mb-6">
                  Click on any available date (green) in the calendar to start your booking process.
                </p>
                <div className="space-y-3 text-left">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-gray-700">Real-time Google Calendar integration</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-gray-700">Instant WhatsApp booking confirmation</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-gray-700">Flexible event customization</span>
                  </div>
                </div>
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-playfair font-semibold text-gray-800">
                    Book Your Event
                  </h3>
                  <button
                    onClick={() => setShowBookingForm(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>

                <div className="mb-6 p-4 bg-gold/10 rounded-lg">
                  <p className="text-gold font-semibold">
                    Selected Date: {selectedDate.toLocaleDateString('en-ZA', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>

                <form onSubmit={handleBookingSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={bookingData.name}
                      onChange={(e) => setBookingData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={bookingData.email}
                      onChange={(e) => setBookingData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={bookingData.phone}
                      onChange={(e) => setBookingData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Event Type *
                    </label>
                    <select
                      value={bookingData.eventType}
                      onChange={(e) => setBookingData(prev => ({ ...prev, eventType: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                      required
                    >
                      <option value="">Select event type</option>
                      <option value="Wedding">Wedding</option>
                      <option value="Corporate Event">Corporate Event</option>
                      <option value="Birthday Party">Birthday Party</option>
                      <option value="Anniversary">Anniversary</option>
                      <option value="Team Building">Team Building</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expected Guest Count *
                    </label>
                    <input
                      type="number"
                      min="10"
                      value={bookingData.guestCount}
                      onChange={(e) => setBookingData(prev => ({ ...prev, guestCount: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Additional Notes
                    </label>
                    <textarea
                      value={bookingData.additionalNotes}
                      onChange={(e) => setBookingData(prev => ({ ...prev, additionalNotes: e.target.value }))}
                      rows="3"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                      placeholder="Tell us about your event vision, special requirements, or any questions you have..."
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full btn-primary flex items-center justify-center space-x-2"
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span>Send Booking Request via WhatsApp</span>
                  </button>
                </form>
              </motion.div>
            )}

            {/* Contact Info */}
            <div className="card bg-gradient-to-br from-gold/10 to-olive/10 border border-gold/20">
              <h4 className="text-lg font-playfair font-semibold text-gray-800 mb-4">
                Need Help?
              </h4>
              <div className="space-y-3 text-sm">
                <div className="flex items-center space-x-3">
                  <MessageCircle className="w-4 h-4 text-gold" />
                  <span className="text-gray-700">WhatsApp: 066 328 6231</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Clock className="w-4 h-4 text-gold" />
                  <span className="text-gray-700">Mon-Fri: 8AM-6PM | Sat: 9AM-4PM</span>
                </div>
                <p className="text-gray-600 text-xs mt-3">
                  We typically respond to booking requests within 2 hours during business hours.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default BookingCalendar;
